import type { CustomerOrder, Product, TrustCenter } from '../types'

export const demoTrustCenter: TrustCenter = {
  companyName: '栖居木作',
  companyIdentifier: '统一社会信用代码：演示资料，正式上线前替换',
  officeAddress: '企业办公地址待正式资料审核后公开',
  factoryAddress: '合作工厂地址待正式资料审核后公开',
  servicePhone: '400-000-0000',
  complaintPhone: '400-000-0000',
  guarantees: [
    {
      title: '未上门可退',
      description: '量房服务开始前，可按页面规则申请退回量房服务费。',
    },
    {
      title: '方案确认后生产',
      description: '尺寸、设计、材质五金和生产规则全部确认后，才进入生产冻结。',
    },
    {
      title: '平台统一受理',
      description: '测量、设计、生产及安装争议由平台统一登记和跟进。',
    },
    {
      title: '过程节点留痕',
      description: '报告、图纸、报价、付款核销和确认记录均保留版本。',
    },
  ],
}

const sharedColors = [
  { id: 'c-ice-oak', code: 'C-ICE-OAK', name: '冰川白橡', supplierCode: 'MU-101', swatch: '#d8d9d4', swatchImage: '' },
  { id: 'c-fog-grey', code: 'C-FOG-GREY', name: '雾灰', supplierCode: 'MU-217', swatch: '#8f9695', swatchImage: '' },
  { id: 'c-forest', code: 'C-FOREST', name: '深林绿', supplierCode: 'MU-318', swatch: '#35554a', swatchImage: '' },
  { id: 'c-walnut', code: 'C-WALNUT', name: '冷胡桃', supplierCode: 'MU-426', swatch: '#62554c', swatchImage: '' },
]

const sharedHardware = [
  {
    id: 'h-standard',
    code: 'HW-STANDARD',
    name: '静音基础方案',
    brand: '合作品牌',
    model: 'H-01',
    description: '缓冲铰链与基础静音导轨，适合日常高频使用。',
    level: '标准' as const,
    isDefault: true,
    optionRole: 'default' as const,
    specifications: [
      { componentKey: 'hinge', label: '铰链', value: '缓冲铰链', note: '日常高频开合' },
      { componentKey: 'rail', label: '轨道', value: '基础静音导轨', note: '' },
      { componentKey: 'safe', label: '保险箱', value: '不含', note: '可按需升级' },
    ],
  },
  {
    id: 'h-premium',
    code: 'HW-PREMIUM',
    name: '耐用升级方案',
    brand: '合作品牌',
    model: 'H-02',
    description: '升级阻尼铰链和全拉出导轨，开合更平稳。',
    level: '升级' as const,
    isDefault: false,
    optionRole: 'upgrade' as const,
    specifications: [
      { componentKey: 'hinge', label: '铰链', value: '升级阻尼铰链', note: '柔和闭合' },
      { componentKey: 'rail', label: '轨道', value: '全拉出静音导轨', note: '加厚承重' },
      { componentKey: 'safe', label: '保险箱', value: '可选嵌入式', note: '需单独确认尺寸' },
    ],
  },
]

const sharedParameterGroups = [
  {
    id: 'parameters-material',
    name: '基础参数',
    items: [
      { id: 'material-board', label: '柜体材质', value: '多层实木板' },
      { id: 'material-thickness', label: '柜体厚度', value: '18 mm' },
    ],
  },
  {
    id: 'parameters-service',
    name: '服务参数',
    items: [
      { id: 'service-production', label: '生产周期', value: '以正式订单确认为准' },
      { id: 'service-install', label: '安装方式', value: '专业人员上门安装' },
    ],
  },
]

export const demoProducts: Product[] = [
  {
    id: 'p-wardrobe',
    slug: 'muyu-wardrobe',
    name: '木屿平开门衣柜',
    sortOrder: 1,
    category: '衣柜',
    secondaryCategory: '平开门衣柜',
    summary: '成熟平开门结构，适合卧室整墙或独立摆放。',
    description:
      '保留稳定柜体结构，仅开放外观颜色、验证范围内的宽高深和整套五金方案。最终尺寸以专业复尺和确认图纸为准。',
    version: 'PV-2026-01',
    versionId: 'pv-wardrobe-demo',
    image: '/assets/product-wardrobe.avif',
    galleryImages: [{ id: 'wardrobe-cover', url: '/assets/product-wardrobe.avif', name: '衣柜主图' }],
    imagePosition: '62% center',
    colors: sharedColors,
    hardwarePackages: sharedHardware,
    dimensionPresets: [
      { id: 'wp-1600', label: '两门组合', widthMm: 1600, heightMm: 2200, depthMm: 600 },
      { id: 'wp-2000', label: '三门组合', widthMm: 2000, heightMm: 2400, depthMm: 600 },
      { id: 'wp-2400', label: '四门组合', widthMm: 2400, heightMm: 2400, depthMm: 600 },
    ],
    dimensionRanges: {
      widthMm: { min: 1400, max: 2600, step: 50, default: 2000 },
      heightMm: { min: 2000, max: 2600, step: 50, default: 2400 },
      depthMm: { min: 550, max: 650, step: 10, default: 600 },
    },
    dimensionReferenceImage: {
      url: '/assets/product-wardrobe.avif',
      name: '宽高深测量参考',
      alt: '衣柜宽高深测量参考图',
    },
    serviceNotes: ['高度超过2400毫米需要工艺复核', '贴墙安装必须专业复尺', '不支持异形墙面现场切割'],
    parameterGroups: sharedParameterGroups,
    specifications: [
      {
        id: 'wardrobe-custom',
        name: '个性选色',
        parts: [
          {
            id: 'cabinet-body',
            name: '柜体颜色',
            allowedColorCodes: sharedColors.map((color) => color.code),
            defaultColorCode: sharedColors[0].code,
            required: true,
            sortOrder: 0,
          },
          {
            id: 'door-panel',
            name: '柜门颜色',
            allowedColorCodes: sharedColors.map((color) => color.code),
            defaultColorCode: sharedColors[3].code,
            required: true,
            sortOrder: 1,
          },
        ],
        sortOrder: 0,
        purchaseMode: 'custom',
        purchaseModeExplicit: true,
      },
    ],
    customizationProfile: {
      schemaVersion: 1,
      specificationId: 'wardrobe-custom',
      specification: {
        id: 'wardrobe-custom',
        name: '个性选色',
        parts: [
          {
            id: 'cabinet-body',
            name: '柜体颜色',
            allowedColorCodes: sharedColors.map((color) => color.code),
            defaultColorCode: sharedColors[0].code,
            required: true,
            sortOrder: 0,
          },
          {
            id: 'door-panel',
            name: '柜门颜色',
            allowedColorCodes: sharedColors.map((color) => color.code),
            defaultColorCode: sharedColors[3].code,
            required: true,
            sortOrder: 1,
          },
        ],
        sortOrder: 0,
        purchaseMode: 'custom',
        purchaseModeExplicit: true,
      },
    },
    skus: [],
  },
  {
    id: 'p-sideboard',
    slug: 'qingshan-sideboard',
    name: '青山餐边柜',
    sortOrder: 2,
    category: '餐边柜',
    secondaryCategory: '落地餐边柜',
    summary: '封闭储物与固定开放区组合，适合餐厅常规墙面。',
    description:
      '以成熟餐边柜为基础，只调整规定范围内的尺寸、板材花色和五金等级，不改变开放区和柜门比例。',
    version: 'PV-2026-01',
    versionId: 'pv-sideboard-demo',
    image: '/assets/product-sideboard.avif',
    galleryImages: [{ id: 'sideboard-cover', url: '/assets/product-sideboard.avif', name: '餐边柜主图' }],
    imagePosition: '42% center',
    colors: sharedColors.slice(0, 3),
    hardwarePackages: sharedHardware,
    dimensionPresets: [
      { id: 'sp-1400', label: '紧凑款', widthMm: 1400, heightMm: 2100, depthMm: 420 },
      { id: 'sp-1800', label: '标准款', widthMm: 1800, heightMm: 2200, depthMm: 450 },
      { id: 'sp-2200', label: '加宽款', widthMm: 2200, heightMm: 2300, depthMm: 450 },
    ],
    dimensionRanges: {
      widthMm: { min: 1200, max: 2400, step: 50, default: 1800 },
      heightMm: { min: 1900, max: 2400, step: 50, default: 2200 },
      depthMm: { min: 400, max: 500, step: 10, default: 450 },
    },
    serviceNotes: ['开放区结构固定', '现场插座位置必须拍照登记', '不包含水电改造'],
    parameterGroups: sharedParameterGroups,
    specifications: [],
    skus: [],
  },
  {
    id: 'p-shoe-cabinet',
    slug: 'xiyu-shoe-cabinet',
    name: '溪屿玄关鞋柜',
    sortOrder: 3,
    category: '鞋柜',
    secondaryCategory: '薄型鞋柜',
    summary: '轻量玄关收纳，预留固定换鞋区和底部常鞋位。',
    description:
      '适合规则玄关墙面。柜内分区保持不变，客户可在限定范围内选择颜色、尺寸和五金方案。',
    version: 'PV-2026-01',
    versionId: 'pv-shoe-demo',
    image: '/assets/product-sideboard.avif',
    galleryImages: [{ id: 'shoe-cover', url: '/assets/product-sideboard.avif', name: '鞋柜主图' }],
    imagePosition: '78% center',
    colors: [sharedColors[0], sharedColors[1], sharedColors[3]],
    hardwarePackages: sharedHardware,
    dimensionPresets: [
      { id: 'sh-1000', label: '单人户型', widthMm: 1000, heightMm: 2100, depthMm: 350 },
      { id: 'sh-1400', label: '标准户型', widthMm: 1400, heightMm: 2200, depthMm: 380 },
    ],
    dimensionRanges: {
      widthMm: { min: 900, max: 1600, step: 50, default: 1400 },
      heightMm: { min: 1900, max: 2400, step: 50, default: 2200 },
      depthMm: { min: 330, max: 420, step: 10, default: 380 },
    },
    serviceNotes: ['深度小于350毫米需要防倾倒评估', '入户门开启范围必须测量', '不支持现场避让强弱电箱'],
    parameterGroups: sharedParameterGroups,
    specifications: [],
    skus: [],
  },
]

export const demoOrders: CustomerOrder[] = [
  {
    id: 'order-demo-1',
    orderNo: 'DG202607260018',
    inquiryStatus: 'accepted',
    productName: '木屿平开门衣柜',
    productImage: '/assets/product-wardrobe.avif',
    city: '杭州市余杭区',
    stage: 'confirmation',
    stageLabel: '待确认最终方案',
    updatedAt: '2026-07-26 15:42',
    nextAction: '核对四部分资料，确认无误后获取验证码。',
    timeline: [
      {
        id: 't1',
        title: '询价已接受',
        description: '产品配置在有限定制范围内。',
        occurredAt: '07-22 10:18',
        state: 'done',
      },
      {
        id: 't2',
        title: '量房已完成',
        description: '专业量房报告已通过内部审核。',
        occurredAt: '07-24 16:30',
        state: 'done',
      },
      {
        id: 't3',
        title: '方案报价完成',
        description: '正式方案和报价已经发送。',
        occurredAt: '07-26 15:42',
        state: 'done',
      },
      {
        id: 't4',
        title: '确认最终方案',
        description: '当前需要客户核对并确认四部分资料。',
        state: 'current',
      },
      {
        id: 't5',
        title: '支付生产定金',
        description: '确认后按页面展示的公司账户付款。',
        state: 'upcoming',
      },
      {
        id: 't6',
        title: '生产冻结',
        description: '财务核销和工艺复核通过后生成冻结资料包。',
        state: 'upcoming',
      },
    ],
    survey: {
      status: 'completed',
      hasBooking: true,
      bookingId: 'survey-demo-1',
      appointment: '2026-07-24 14:00',
      surveyor: '陈师傅',
      reportVersion: 'LF-20260724-02',
    },
    confirmation: {
      packageNo: 'QR202607260006',
      status: 'waiting',
      sections: [
        {
          id: 'dimensions',
          title: '尺寸确认',
          status: 'ready',
          items: [
            { label: '成品尺寸', value: '宽 2000 × 高 2400 × 深 600 毫米' },
            { label: '量房报告', value: 'LF-20260724-02' },
            { label: '现场条件', value: '贴墙安装，右侧预留 30 毫米收口' },
          ],
        },
        {
          id: 'design',
          title: '设计方案确认',
          status: 'ready',
          items: [
            { label: '图纸版本', value: 'TZ-20260726-03' },
            { label: '开门方向', value: '左侧门左开，其余右开' },
            { label: '内部结构', value: '标准固定结构，不做改动' },
          ],
        },
        {
          id: 'materials',
          title: '材质及五金确认',
          status: 'ready',
          items: [
            { label: '颜色', value: '冰川白橡 MU-101' },
            { label: '五金', value: '耐用升级方案 H-02' },
            { label: '封边', value: '同色封边' },
          ],
        },
        {
          id: 'rules',
          title: '生产与退款规则确认',
          status: 'ready',
          items: [
            { label: '生产冻结点', value: '生产定金核销和工艺复核完成后' },
            { label: '改动规则', value: '冻结后改动按实际损失重新核算' },
            { label: '保障规则', value: '安心装保障 2026-07 版' },
          ],
        },
      ],
    },
    payments: [
      { id: 'pay-survey', title: '量房服务费', status: 'verified', amountLabel: '已核销' },
      { id: 'pay-deposit', title: '生产定金', status: 'waiting', amountLabel: '确认后展示' },
    ],
  },
  {
    id: 'order-demo-2',
    orderNo: 'DG202607210009',
    inquiryStatus: 'accepted',
    productName: '青山餐边柜',
    productImage: '/assets/product-sideboard.avif',
    city: '宁波市鄞州区',
    stage: 'survey',
    stageLabel: '待安排量房',
    updatedAt: '2026-07-25 09:20',
    nextAction: '量房服务费已核销，工作人员将在一个工作日内确认上门时间。',
    timeline: [
      {
        id: 't1',
        title: '询价已接受',
        description: '已确认服务区域和产品定制边界。',
        occurredAt: '07-21 11:02',
        state: 'done',
      },
      {
        id: 't2',
        title: '量房服务费已核销',
        description: '财务已确认实际到账。',
        occurredAt: '07-25 09:20',
        state: 'done',
      },
      {
        id: 't3',
        title: '安排专业量房',
        description: '正在匹配本地量房人员。',
        state: 'current',
      },
      {
        id: 't4',
        title: '方案与报价',
        description: '量房报告通过后开始制作。',
        state: 'upcoming',
      },
    ],
    survey: { status: 'pending', hasBooking: true, bookingId: 'survey-demo-2' },
    confirmation: { status: 'preparing', sections: [] },
    payments: [
      { id: 'pay-survey', title: '量房服务费', status: 'verified', amountLabel: '已核销' },
    ],
  },
]
